<script setup lang="ts">
import { computed } from 'vue'
import Icon from '@/components/Icon.vue'
import type { IconName } from '@/components/icons'

type AlertTone = 'info' | 'success' | 'warning' | 'danger'

const toneIcons: Record<AlertTone, IconName> = {
  info: 'alert',
  success: 'check',
  warning: 'alert',
  danger: 'alert',
}

const props = withDefaults(defineProps<{ tone?: AlertTone; title?: string }>(), { tone: 'info' })

const icon = computed(() => toneIcons[props.tone])
</script>

<template>
  <div class="alert" :class="tone" :role="tone === 'danger' ? 'alert' : 'status'">
    <Icon :name="icon" :size="18" class="icon" />
    <div class="body">
      <strong v-if="title" class="title">{{ title }}</strong>
      <div class="text"><slot /></div>
    </div>
  </div>
</template>

<style scoped>
.alert {
  display: flex;
  gap: var(--space-2xs);
  padding: var(--space-xs) var(--space-sm);
  font-size: var(--text-sm);
  line-height: var(--leading-snug);
  border: var(--border-width) solid;
  border-radius: var(--radius-sm);
}

.icon {
  flex: none;
  margin-top: 0.1em;
}

.body {
  display: flex;
  flex-direction: column;
  gap: var(--space-3xs);
  min-width: 0;
}

.title {
  font-weight: var(--weight-semibold);
  color: inherit;
}

.text {
  color: inherit;
  opacity: 0.9;
}

.info {
  color: var(--color-accent-hover);
  background-color: var(--color-accent-soft);
  border-color: color-mix(in srgb, var(--color-accent) 25%, transparent);
}

.success {
  color: var(--color-success);
  background-color: color-mix(in srgb, var(--color-success) 10%, transparent);
  border-color: color-mix(in srgb, var(--color-success) 30%, transparent);
}

.warning {
  color: var(--color-accent-2);
  background-color: var(--color-accent-2-soft);
  border-color: color-mix(in srgb, var(--color-accent-2) 30%, transparent);
}

.danger {
  color: var(--color-danger);
  background-color: var(--color-danger-soft);
  border-color: color-mix(in srgb, var(--color-danger) 30%, transparent);
}
</style>
